import { useEffect, useState } from 'react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import ArticleList from '../components/ArticleList';
import ArticleReader from '../components/ArticleReader';
import SharedArticles from '../components/SharedArticles';
import UserProfile from '../components/UserProfile';
import AccountSettings from '../components/AccountSettings';
import CommandModal from '../components/CommandModal';
import DigestList from '../components/DigestList';
import DigestReader from '../components/DigestReader';
import UserMenu from '../components/UserMenu';
import Users from './Users';
import DiscoverFeeds from './DiscoverFeeds';
import Export from './Export';
import AdminPanel from './AdminPanel';
import CollaborativePosts from './CollaborativePosts';
import CollaborativePostDetail from './CollaborativePostDetail';
import MobileHeader from '../components/MobileHeader';
import MobileBottomNav from '../components/MobileBottomNav';
import MobileFeedDrawer from '../components/MobileFeedDrawer';
import WelcomeTour from '../components/WelcomeTour';
import AnnouncementDisplay from '../components/AnnouncementDisplay';
import ShortcutsModal from '../components/ShortcutsModal';
import KeyboardHints from '../components/KeyboardHints';
import { feeds as feedsApi, articles as articlesApi } from '../lib/api';
import { useAuthStore, useFeedsStore, useArticlesStore, useLikedArticlesStore } from '../store/useStore';
import toast from 'react-hot-toast';

// Mono breadcrumb for the desktop top bar
function sectionLabel(pathname: string): string {
  if (pathname.startsWith('/read') || pathname.startsWith('/article/')) return 'READ';
  if (pathname.startsWith('/friends')) return 'FRIENDS';
  if (pathname.startsWith('/digest')) return 'DIGEST';
  if (pathname.startsWith('/users') || pathname.startsWith('/user/')) return 'USERS';
  if (pathname.startsWith('/collaborate')) return 'COLLABORATIVE · BETA';
  if (pathname.startsWith('/settings')) return 'SETTINGS';
  if (pathname.startsWith('/discover')) return 'DISCOVER';
  if (pathname.startsWith('/export')) return 'EXPORT';
  if (pathname.startsWith('/admin')) return 'ADMIN';
  return '';
}

export default function Dashboard() {
  const location = useLocation();
  const token = useAuthStore((state) => state.token)!;
  const user = useAuthStore((state) => state.user);
  const setFeeds = useFeedsStore((state) => state.setFeeds);
  const setArticles = useArticlesStore((state) => state.setArticles);
  const setCurrentContext = useArticlesStore((state) => state.setCurrentContext);
  const selectedFeedId = useFeedsStore((state) => state.selectedFeedId);
  const selectedFolderId = useFeedsStore((state) => state.selectedFolderId);
  const filter = useArticlesStore((state) => state.filter);
  const bucket = useArticlesStore((state) => state.bucket);
  const searchQuery = useArticlesStore((state) => state.searchQuery);
  const { addLikedArticle } = useLikedArticlesStore();
  const [loading, setLoading] = useState(true);
  const [showCommandModal, setShowCommandModal] = useState(false);
  const [showMobileDrawer, setShowMobileDrawer] = useState(false);
  const [showShortcutsModal, setShowShortcutsModal] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);

  // Load feeds on mount
  useEffect(() => {
    async function loadFeeds() {
      try {
        const feedsData = await feedsApi.getAll(token);
        setFeeds(feedsData);
      } catch {
        toast.error('Failed to load feeds');
      } finally {
        setLoading(false);
      }
    }

    loadFeeds();
  }, [token, setFeeds]);

  // A manual refresh from the sidebar / header asks the list to reload
  useEffect(() => {
    const bump = () => setReloadTick((n) => n + 1);
    window.addEventListener('feeds-refreshed', bump);
    return () => window.removeEventListener('feeds-refreshed', bump);
  }, []);

  // Load articles when feed, filter or bucket changes
  useEffect(() => {
    async function loadArticles() {
      try {
        setArticles([]);

        const params: any = { limit: 100 };

        if (selectedFeedId) {
          params.feedId = selectedFeedId;
        }

        if (selectedFolderId) {
          params.folderId = selectedFolderId;
        }

        if (filter === 'unread') {
          params.unread = true;
        } else if (filter === 'liked') {
          params.liked = true;
        } else if (filter === 'archive') {
          params.archive = 'true';
        }

        // Bucket only applies inside the fresh window (not archive, not liked).
        if (bucket && filter !== 'archive' && filter !== 'liked') {
          params.bucket = bucket;
        }

        if (searchQuery) {
          params.search = searchQuery;
        }

        const { articles: newArticles } = await articlesApi.getAll(token, params);

        setArticles(newArticles);
        setCurrentContext('articles');

        newArticles.forEach(article => {
          if (article.isLiked) {
            addLikedArticle(article.id);
          }
        });
      } catch {
        toast.error('Failed to load articles');
      }
    }

    if (!loading && location.pathname === '/read') {
      loadArticles();
    }
  }, [token, selectedFeedId, selectedFolderId, filter, bucket, searchQuery, loading, setArticles, setCurrentContext, location.pathname, addLikedArticle, reloadTick]);

  // Global keyboard shortcuts
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (document.activeElement?.tagName === 'INPUT' ||
          document.activeElement?.tagName === 'TEXTAREA' ||
          showCommandModal ||
          showShortcutsModal) {
        return;
      }

      if (e.key === '/') {
        e.preventDefault();
        setShowCommandModal(true);
      }

      if (e.key === '?' || (e.shiftKey && e.key === '/')) {
        e.preventDefault();
        setShowShortcutsModal(true);
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showCommandModal, showShortcutsModal]);

  const isReadList = location.pathname === '/read';

  return (
    <div className="flex h-dvh bg-canvas">
      <Sidebar />

      <MobileHeader onMenuClick={() => setShowMobileDrawer(true)} />

      <MobileFeedDrawer
        isOpen={showMobileDrawer}
        onClose={() => setShowMobileDrawer(false)}
      />

      {/* Main column */}
      <div className="flex-1 min-w-0 flex flex-col overflow-y-auto pt-12 lg:pt-0 pb-14 lg:pb-0 bg-canvas">
        {/* Desktop top bar: section kicker on the left rail, account on the right */}
        <div className="hidden lg:flex shrink-0 items-center justify-between h-12 pl-6 pr-3 border-b border-edge bg-canvas">
          <span className="kicker">{sectionLabel(location.pathname)}</span>
          <UserMenu />
        </div>

        <Routes>
          <Route path="/" element={<Navigate to="/read" replace />} />
          <Route path="/read" element={<ArticleList />} />
          <Route path="/articles" element={<Navigate to="/read" replace />} />
          <Route path="/friends" element={<SharedArticles />} />
          <Route path="/shares" element={<Navigate to="/friends" replace />} />
          <Route path="/article/:id" element={<ArticleReader />} />
          <Route path="/digest" element={<DigestList />} />
          <Route path="/digest/:id" element={<DigestReader />} />
          <Route path="/collaborate" element={<CollaborativePosts />} />
          <Route path="/collaborate/:id" element={<CollaborativePostDetail />} />
          <Route path="/collaborate/edit/:id" element={<CollaborativePostDetail />} />
          <Route path="/users" element={<Users />} />
          <Route path="/user/:userId" element={<UserProfile />} />
          <Route path="/discover" element={<DiscoverFeeds />} />
          <Route path="/settings" element={<AccountSettings />} />
          <Route path="/export" element={<Export />} />
          <Route
            path="/admin"
            element={user?.isAdmin ? <AdminPanel /> : <Navigate to="/read" replace />}
          />
        </Routes>

        {/* Keyboard hints: reader list only, desktop only. Sits at the foot of the
            column (sticky while the flat list scrolls) so the paged list ends above it. */}
        {isReadList && (
          <div className="hidden lg:block shrink-0 sticky bottom-0 z-10">
            <KeyboardHints onOpenShortcuts={() => setShowShortcutsModal(true)} />
          </div>
        )}
      </div>

      <MobileBottomNav onSearch={() => setShowCommandModal(true)} />

      <CommandModal
        isOpen={showCommandModal}
        onClose={() => setShowCommandModal(false)}
      />

      <ShortcutsModal
        isOpen={showShortcutsModal}
        onClose={() => setShowShortcutsModal(false)}
      />

      <WelcomeTour />
      <AnnouncementDisplay />
    </div>
  );
}
